import { useMemo, useRef, useState } from 'react';
import { renderProjectFilterOptionLabel } from '@/features/projects/components/ProjectFilterOptionLabel';
import { getFilterEmptyLabel, getFilterLabel } from '@/shared/copy/filters';
import { componentSizes } from '@/theme/componentSizes';
import { isLightTheme } from '@/theme/semanticTokens';
import { r, s, typographyTokens } from '@/theme/tokens';
import { SearchFilterField } from '@/ui/composites/filters/SearchFilterField';
import { withStatusFilterOptionIcons } from '@/ui/composites/filters/statusFilterOptionIcons';
import { TagsFilterField } from '@/ui/composites/filters/TagsFilterField';
import { Box, Button, ReplayOutlinedIcon, Skeleton, Text } from '@/ui/primitives';
import {
  buildToolbarResetState,
  buildToolbarSummaryState,
  type ToolbarSummaryState,
} from './SearchTagsDateHeaderFiltersNextState';
import type { SearchTagsDateHeaderFiltersProps } from './SearchTagsDateHeaderFilters';
import type { QualityRange } from '@/features/quality/hooks';
import type { TagColor } from '@/shared/data/tags';
import type { TagsFilterFieldProps } from '@/ui/composites/filters/TagsFilterFieldTypes';
import type { ProjectDto } from '@flowqa/shared';
import type { ReactElement } from 'react';

export type ToolbarDropdownOption = {
  value: string;
  label: string;
  count?: number;
  color?: TagColor;
  badgeChars?: ProjectDto['badgeChars'];
  badgeColor?: ProjectDto['badgeColor'];
  badgeEnabled?: ProjectDto['badgeEnabled'];
};
export type ToolbarFilterSlot = {
  id: string;
  label: string;
  inputAriaLabel?: string;
  value: string[];
  defaultValue?: string[];
  options: ToolbarDropdownOption[];
  onChange: (nextValues: string[]) => void;
  emptyLabel?: string;
  dropdownWidthPx?: number;
  multiple?: boolean;
  renderOptionLabel?: TagsFilterFieldProps['renderOptionLabel'];
};
type ToolbarFilterDropdownId = string;
type DateDropdownOption = { value: QualityRange | 'custom'; label: string };

const defaultDateDropdownOptions = [
  { value: 'latest', label: 'Latest' },
  { value: '7d', label: 'Last 7 Days' },
  { value: '14d', label: 'Last 14 Days' },
  { value: '30d', label: 'Last 30 Days' },
  { value: '90d', label: 'Last 90 Days' },
] satisfies DateDropdownOption[];

type SearchTagsDateHeaderFiltersNextProps = Omit<
  SearchTagsDateHeaderFiltersProps,
  'projectOptions' | 'statusOptions' | 'tagOptions'
> & {
  tagOptions: ToolbarDropdownOption[];
  projectOptions?: ToolbarDropdownOption[];
  statusOptions?: ToolbarDropdownOption[];
  selectedSuites?: string[];
  onSuitesChange?: (nextSuites: string[]) => void;
  suiteOptions?: ToolbarDropdownOption[];
  suiteLabel?: string;
  selectedEntities?: string[];
  onEntitiesChange?: (nextEntities: string[]) => void;
  entityOptions?: ToolbarDropdownOption[];
  entityLabel?: string;
  selectedItems?: string[];
  onItemsChange?: (nextItems: string[]) => void;
  itemOptions?: ToolbarDropdownOption[];
  itemLabel?: string;
  summaryCount?: number | null;
  summaryStartDay?: string | null;
  summaryEndDay?: string | null;
  summarySingularLabel?: string;
  summaryPluralLabel?: string;
  summaryRelatedEntityLabel?: string | null;
  summaryEmptyText?: string | null;
  summaryLoading?: boolean;
  dateQuickFilterOptions?: QualityRange[];
  filterSlots?: ToolbarFilterSlot[];
  showSearch?: boolean;
  showDateFilter?: boolean;
  showCustomDateRange?: boolean;
  onCustomDateRangeOpen?: (anchorEl: HTMLElement | null) => void;
};

type ToolbarFilterOpenProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

type ToolbarDropdownFilterProps = Omit<ToolbarFilterSlot, 'id'> & {
  id: ToolbarFilterDropdownId;
  selectedValueLeftPadding?: string;
  optionPresentation?: TagsFilterFieldProps['optionPresentation'];
  openProps: ToolbarFilterOpenProps;
};

const toolbarControlHeight = componentSizes.filters.toolbarControlHeight;
const toolbarControlRadius = r('sm');
const toolbarControlFontSize = componentSizes.filters.toolbarFontSize;
const toolbarPopoverFontSize = componentSizes.filters.toolbarPopoverFontSize;
const toolbarResetIconSize = componentSizes.filters.toolbarResetIconSize;
const toolbarControlBorderColor = 'var(--surface-border)';
const summarySkeletonWidth = 'min(100%, 760px)';
const summarySlotOffset = s('sm');
const summarySlotBottomCompensation = s('md');

const nextToolbarControlSx = {
  '& .MuiInputBase-root, & .MuiOutlinedInput-root': {
    minHeight: `${toolbarControlHeight}px`,
    height: `${toolbarControlHeight}px`,
    borderRadius: toolbarControlRadius,
    backgroundColor: 'var(--surface-card)',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderRadius: toolbarControlRadius,
    borderColor: toolbarControlBorderColor,
  },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
    {
      borderColor: toolbarControlBorderColor,
      borderWidth: '1px',
    },
  '& .MuiInputAdornment-positionStart .MuiSvgIcon-root': {
    width: 20,
    height: 20,
  },
  '& .MuiInputAdornment-positionStart': {
    marginLeft: s('none'),
    marginRight: s('sm'),
  },
  '& .MuiInputBase-root, & .MuiOutlinedInput-root, & .MuiInputBase-input, & .MuiOutlinedInput-input, & input':
    {
      fontSize: `${toolbarControlFontSize}px`,
    },
  '& .MuiInputBase-input::placeholder, & .MuiOutlinedInput-input::placeholder, & input::placeholder':
    {
      fontSize: `${toolbarControlFontSize}px`,
    },
} as const;

const nextFilterBackgroundColor = 'var(--surface-card)';
const nextFilterDropdownBackgroundColor = 'var(--surface-card)';
const nextFilterInputLeftPadding = s('md');
const nextSelectedFilterValueLeftPadding = s('xs');

const toToolbarEmptyLabel = (label: string): string => {
  if (label.endsWith('us')) return `${label}es`;
  if (/[b-df-hj-np-tv-z]y$/i.test(label)) return `${label.slice(0, -1)}ies`;
  if (label.endsWith('s')) return label;
  return `${label}s`;
};

const ToolbarDropdownFilter = ({
  id,
  label,
  inputAriaLabel,
  value,
  defaultValue,
  options,
  onChange,
  emptyLabel,
  dropdownWidthPx,
  multiple,
  renderOptionLabel,
  selectedValueLeftPadding,
  optionPresentation,
  openProps,
}: ToolbarDropdownFilterProps): ReactElement => (
  <Box key={id} sx={{ width: { xs: '100%', sm: 160 }, flex: '0 0 auto' }}>
    <TagsFilterField
      label={label}
      inputAriaLabel={inputAriaLabel ?? `Filter by ${label.toLowerCase()}`}
      value={value}
      defaultValue={defaultValue}
      options={options}
      renderOptionLabel={renderOptionLabel}
      onChange={onChange}
      emptyLabel={emptyLabel ?? toToolbarEmptyLabel(label)}
      fullWidth
      showLabel={false}
      multiple={multiple}
      fontSizePx={toolbarControlFontSize}
      optionFontSizePx={toolbarPopoverFontSize}
      labelControlGap={s('xs')}
      controlHeight={toolbarControlHeight}
      controlRadius={toolbarControlRadius}
      dropdownWidthPx={dropdownWidthPx}
      showClearButton={false}
      optionPresentation={optionPresentation}
      {...openProps}
      inputLeftPadding={nextFilterInputLeftPadding}
      selectedValueLeftPadding={selectedValueLeftPadding}
      inputBackgroundColor={nextFilterBackgroundColor}
      inputBorderColor={toolbarControlBorderColor}
      dropdownBackgroundColor={nextFilterDropdownBackgroundColor}
    />
  </Box>
);

const ToolbarSummary = ({ showSkeleton, text }: ToolbarSummaryState): ReactElement => (
  <Box
    sx={{
      minHeight: (theme) => `calc(${theme.typography.pxToRem(13)} * 1.4 + ${summarySlotOffset})`,
      display: 'flex',
      alignItems: 'center',
      pt: summarySlotOffset,
      width: '100%',
    }}
  >
    {showSkeleton ? (
      <Skeleton
        variant="rounded"
        width={summarySkeletonWidth}
        height={14}
        sx={{ borderRadius: r('pill') }}
      />
    ) : text ? (
      <Text
        role="bodyMuted"
        sx={{
          fontSize: (theme) => theme.typography.pxToRem(13),
          lineHeight: 1.4,
          textAlign: 'left',
          maxWidth: '100%',
          color: (theme) => theme.app.colors.textMuted,
          opacity: 0.94,
        }}
      >
        {text}
      </Text>
    ) : null}
  </Box>
);

export const SearchTagsDateHeaderFiltersNext = ({
  searchValue,
  onSearchChange,
  searchAriaLabel,
  selectedTags,
  onTagsChange,
  tagOptions,
  tagOptionsLoading,
  selectedProjects,
  onProjectsChange,
  projectOptions,
  selectedSuites,
  onSuitesChange,
  suiteOptions,
  suiteLabel = 'Suite',
  selectedEntities,
  onEntitiesChange,
  entityOptions,
  entityLabel = 'Item',
  selectedItems,
  onItemsChange,
  itemOptions,
  itemLabel = 'Item',
  selectedStatuses,
  onStatusesChange,
  statusOptions,
  range,
  onRangeChange,
  hasCustomDates,
  isLatestMode,
  onSelectedStartDayChange,
  onSelectedEndDayChange,
  includeLatest = true,
  actions,
  summaryCount = null,
  summaryStartDay = null,
  summaryEndDay = null,
  summarySingularLabel = 'run',
  summaryPluralLabel = 'runs',
  summaryRelatedEntityLabel = null,
  summaryEmptyText = null,
  summaryLoading = false,
  dateQuickFilterOptions,
  filterSlots = [],
  showSearch = true,
  showDateFilter = true,
  showCustomDateRange = true,
  onCustomDateRangeOpen,
}: SearchTagsDateHeaderFiltersNextProps): ReactElement => {
  const dateControlRef = useRef<HTMLDivElement | null>(null);
  const [openFilterDropdown, setOpenFilterDropdown] = useState<ToolbarFilterDropdownId | null>(
    null,
  );
  const showProjectFilter =
    selectedProjects !== undefined &&
    onProjectsChange !== undefined &&
    projectOptions !== undefined;
  const showStatusFilter =
    selectedStatuses !== undefined && onStatusesChange !== undefined && statusOptions !== undefined;
  const statusOptionsWithIcons = useMemo(
    () => (statusOptions ? withStatusFilterOptionIcons(statusOptions) : []),
    [statusOptions],
  );
  const showSuiteFilter =
    selectedSuites !== undefined && onSuitesChange !== undefined && suiteOptions !== undefined;
  const showEntityFilter =
    selectedEntities !== undefined && onEntitiesChange !== undefined && entityOptions !== undefined;
  const showItemFilter =
    selectedItems !== undefined && onItemsChange !== undefined && itemOptions !== undefined;
  const dateOptionValues = new Set(dateQuickFilterOptions);
  const dateOptions = [
    ...defaultDateDropdownOptions.filter((option) => {
      if (option.value === 'latest' && !includeLatest) return false;
      return dateQuickFilterOptions === undefined || dateOptionValues.has(option.value);
    }),
    ...(showCustomDateRange
      ? [{ value: 'custom', label: 'Custom' } satisfies DateDropdownOption]
      : []),
  ];
  const selectedDateRange =
    hasCustomDates && !isLatestMode
      ? ['custom']
      : dateOptions.some((option) => option.value === range)
        ? [range]
        : [];
  const dateLabel =
    selectedDateRange[0] === 'custom'
      ? 'Custom'
      : (dateOptions.find((option) => option.value === range)?.label ?? 'Date');
  const defaultRange = '7d';
  const { hasDirtyToolbarState } = buildToolbarResetState({
    showSearch,
    searchValue,
    selectedTags,
    selectedStatuses,
    selectedProjects,
    selectedSuites,
    selectedEntities,
    selectedItems,
    filterSlots,
    hasCustomDates: showDateFilter ? hasCustomDates : false,
    range: showDateFilter ? range : defaultRange,
    defaultRange,
  });
  const resetToolbar = (): void => {
    if (!hasDirtyToolbarState) return;
    onSearchChange('');
    onTagsChange([]);
    onStatusesChange?.([]);
    onProjectsChange?.([]);
    onSuitesChange?.([]);
    onEntitiesChange?.([]);
    onItemsChange?.([]);
    filterSlots.forEach((filterSlot) => {
      filterSlot.onChange(filterSlot.defaultValue ?? []);
    });
    if (showDateFilter) {
      onSelectedStartDayChange(null);
      onSelectedEndDayChange(null);
      onRangeChange(defaultRange);
    }
  };
  const summaryState = buildToolbarSummaryState({
    summaryCount,
    summaryStartDay,
    summaryEndDay,
    summarySingularLabel,
    summaryPluralLabel,
    summaryRelatedEntityLabel,
    summaryEmptyText,
    summaryLoading,
    isLatestMode,
  });
  const getFilterOpenProps = (id: ToolbarFilterDropdownId): ToolbarFilterOpenProps => ({
    open: openFilterDropdown === id,
    onOpenChange: (nextOpen) => {
      setOpenFilterDropdown((currentOpenDropdown) => {
        if (nextOpen) return id;
        return currentOpenDropdown === id ? null : currentOpenDropdown;
      });
    },
  });

  return (
    <Box
      sx={{
        width: '100%',
        mb: summaryState.showSlot ? `calc(-1 * ${summarySlotBottomCompensation})` : undefined,
      }}
    >
      <Box
        aria-label="Next page toolbar preview"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: s('md'),
          width: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: s('lg'),
            flexWrap: 'wrap',
            minWidth: 0,
            width: 'fit-content',
            maxWidth: '100%',
          }}
        >
          {showSearch ? (
            <Box
              sx={{
                width: { xs: '100%', sm: 220, md: 260 },
                minWidth: { xs: '100%', sm: 220 },
                flex: { xs: '1 1 100%', sm: '0 1 260px' },
              }}
            >
              <SearchFilterField
                value={searchValue}
                inputAriaLabel={searchAriaLabel}
                compact
                fullWidth
                controlHeight={toolbarControlHeight}
                leadingSearchAdornment
                trailingSearchAdornment={false}
                placeholder="Search..."
                onChange={(event) => {
                  onSearchChange(event.target.value);
                }}
                sx={nextToolbarControlSx}
              />
            </Box>
          ) : null}
          {showProjectFilter ? (
            <ToolbarDropdownFilter
              id="project"
              label={getFilterLabel('project')}
              inputAriaLabel="Filter by project"
              value={selectedProjects}
              options={projectOptions}
              renderOptionLabel={renderProjectFilterOptionLabel}
              onChange={onProjectsChange}
              emptyLabel="Projects"
              dropdownWidthPx={200}
              openProps={getFilterOpenProps('project')}
            />
          ) : null}
          {showSuiteFilter ? (
            <ToolbarDropdownFilter
              id="suite"
              label={suiteLabel}
              value={selectedSuites}
              options={suiteOptions}
              onChange={onSuitesChange}
              openProps={getFilterOpenProps('suite')}
            />
          ) : null}
          {showEntityFilter ? (
            <ToolbarDropdownFilter
              id="entity"
              label={entityLabel}
              value={selectedEntities}
              options={entityOptions}
              onChange={onEntitiesChange}
              openProps={getFilterOpenProps('entity')}
            />
          ) : null}
          {showItemFilter ? (
            <ToolbarDropdownFilter
              id="item"
              label={itemLabel}
              value={selectedItems}
              options={itemOptions}
              onChange={onItemsChange}
              openProps={getFilterOpenProps('item')}
            />
          ) : null}
          {filterSlots.map((filterSlot) => (
            <ToolbarDropdownFilter
              key={filterSlot.id}
              {...filterSlot}
              id={`custom:${filterSlot.id}`}
              openProps={getFilterOpenProps(`custom:${filterSlot.id}`)}
            />
          ))}
          {showStatusFilter ? (
            <ToolbarDropdownFilter
              id="status"
              label={getFilterLabel('status')}
              inputAriaLabel="Filter by status"
              value={selectedStatuses}
              options={statusOptionsWithIcons}
              onChange={onStatusesChange}
              emptyLabel="Statuses"
              selectedValueLeftPadding={nextSelectedFilterValueLeftPadding}
              openProps={getFilterOpenProps('status')}
            />
          ) : null}
          <ToolbarDropdownFilter
            id="tags"
            label={getFilterLabel('tags')}
            inputAriaLabel="Filter by tags"
            value={selectedTags}
            options={tagOptions}
            onChange={onTagsChange}
            emptyLabel={
              tagOptionsLoading ? getFilterEmptyLabel('loadingTags') : getFilterLabel('tags')
            }
            selectedValueLeftPadding={nextSelectedFilterValueLeftPadding}
            optionPresentation="tagChip"
            openProps={getFilterOpenProps('tags')}
          />
          {showDateFilter ? (
            <Box ref={dateControlRef} sx={{ width: { xs: '100%', sm: 160 }, flex: '0 0 auto' }}>
              <TagsFilterField
                label="Date"
                inputAriaLabel="Filter by date"
                value={selectedDateRange}
                defaultValue={[defaultRange]}
                options={dateOptions}
                onChange={(nextRange) => {
                  if (nextRange.length === 0) return;
                  const nextValue = nextRange[nextRange.length - 1];
                  if (nextValue === 'custom') return;
                  onSelectedStartDayChange(null);
                  onSelectedEndDayChange(null);
                  onRangeChange(nextValue as typeof range);
                }}
                onOptionSelect={(optionValue) => {
                  if (optionValue !== 'custom') return undefined;
                  onCustomDateRangeOpen?.(dateControlRef.current);
                  return false;
                }}
                emptyLabel={dateLabel}
                fullWidth
                showLabel={false}
                multiple={false}
                fontSizePx={toolbarControlFontSize}
                optionFontSizePx={toolbarPopoverFontSize}
                labelControlGap={s('xs')}
                controlHeight={toolbarControlHeight}
                controlRadius={toolbarControlRadius}
                showClearButton={false}
                {...getFilterOpenProps('date')}
                inputLeftPadding={nextFilterInputLeftPadding}
                inputBackgroundColor={nextFilterBackgroundColor}
                inputBorderColor={toolbarControlBorderColor}
                dropdownBackgroundColor={nextFilterDropdownBackgroundColor}
              />
            </Box>
          ) : null}
          <Button
            variant="outlined"
            fullWidthOnMobile={false}
            onClick={resetToolbar}
            disabled={!hasDirtyToolbarState}
            sx={{
              minHeight: toolbarControlHeight,
              height: toolbarControlHeight,
              borderRadius: toolbarControlRadius,
              borderColor: (theme) =>
                hasDirtyToolbarState ? theme.app.colors.primary : toolbarControlBorderColor,
              color: 'primary.main',
              px: s('md'),
              backgroundColor: nextFilterBackgroundColor,
              fontSize: `${toolbarControlFontSize}px`,
              fontWeight: typographyTokens.bodyMedium.fontWeight,
              '& .MuiSvgIcon-root': {
                color: 'primary.main',
                fontSize: 16,
              },
              '&.Mui-disabled': {
                backgroundColor: (theme) =>
                  isLightTheme(theme) ? 'action.disabledBackground' : undefined,
                borderColor: (theme) =>
                  isLightTheme(theme) ? theme.app.colors.border : toolbarControlBorderColor,
                color: (theme) => (isLightTheme(theme) ? 'text.disabled' : undefined),
                opacity: (theme) => (isLightTheme(theme) ? 0.62 : undefined),
              },
              '&.Mui-disabled .MuiSvgIcon-root': {
                color: 'text.disabled',
              },
            }}
          >
            <Box
              component="span"
              sx={{
                alignItems: 'center',
                display: 'inline-flex',
                flex: '0 0 auto',
                height: toolbarResetIconSize,
                mr: s('sm'),
                width: toolbarResetIconSize,
                '& .MuiSvgIcon-root': {
                  height: toolbarResetIconSize,
                  width: toolbarResetIconSize,
                },
              }}
            >
              <ReplayOutlinedIcon />
            </Box>
            Reset
          </Button>
        </Box>
        {summaryState.showSlot ? <ToolbarSummary {...summaryState} /> : null}
        {actions ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              '& .MuiButton-root': {
                minHeight: toolbarControlHeight,
                height: toolbarControlHeight,
              },
            }}
          >
            {actions}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};
